// src/module_cosmetics/cosmetics_Userhomedash.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

import {
  FaUsers,
  FaTruck,
  FaBoxes,
  FaChartLine,
  FaShoppingCart,
  FaCalendarAlt,
  FaTimes,
  FaCheckCircle,
  FaExclamationTriangle,
  FaFilter,
  FaWrench,
  FaExchangeAlt,
  FaUserCog,
  FaArrowCircleLeft,
  FaMoneyBillWave,
  FaQrcode,
  FaHome,
  FaEllipsisH,
  FaBell,
  FaUserPlus,
} from "react-icons/fa";
import { MdInventory, MdCategory } from "react-icons/md";
import { FiAlertCircle, FiTrendingUp } from "react-icons/fi";
import { useAuth } from '../module_roots/AuthContext';
import {
  fetchDashboardData,
  setAuthData,
  fetchServicesData,
  fetchMobchangeData,
  fetchReturnsData,
  fetchEmployeesData,
  fetchTransactionsData,
  fetchPurchasesData,
} from "./cosmetics_api.js";

// ==================== PAKISTANI CURRENCY FORMATTER ====================
const formatPKR = (value) => {
  const num = parseFloat(value);
  if (isNaN(num) || num === null || num === undefined) {
    return "₨0";
  }
  return new Intl.NumberFormat("en-PK", {
    style: "currency",
    currency: "PKR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(num)
    .replace("PKR", "₨");
};

// ==================== TRANSLATION SYSTEM ====================
const createDashboardTranslator = () => {
  let cache = new Map();

  const loadFromCache = () => {
    try {
      const cached = localStorage.getItem("ourzest_dashboard_translations");
      if (cached) {
        const parsed = JSON.parse(cached);
        Object.entries(parsed).forEach(([key, value]) => {
          cache.set(key, value);
        });
      }
    } catch (e) {
      console.log("No cache found for Dashboard");
    }
  };

  const saveToCache = () => {
    try {
      const cacheObj = {};
      cache.forEach((value, key) => {
        cacheObj[key] = value;
      });
      localStorage.setItem("ourzest_dashboard_translations", JSON.stringify(cacheObj));
    } catch (e) {
      console.error("Error saving to cache:", e);
    }
  };

  const translateAPI = async (text, targetLang) => {
    if (targetLang === "en") return text;

    const dict = {
      ur: {
        "Dashboard": "ڈیش بورڈ",
        "Welcome": "خوش آمدید",
        "Services": "خدمات",
        "Returns": "واپسی",
        "Employees": "ملازمین",
        "Transactions": "لین دین",
        "Recent Sales": "حالیہ فروخت",
        "Recent Purchases": "حالیہ خریداری",
        "Low Stock Alerts": "کم اسٹاک الرٹس",
        "Today's Sales": "آج کی فروخت",
        "Total Customers": "کل گاہک",
        "Suppliers": "سپلائرز",
        "Products": "پروڈکٹس",
        "Inventory Value": "انوینٹری ویلیو",
        "Low Stock Alert": "کم اسٹاک الرٹ",
        "Pending": "زیر التواء",
        "Completed": "مکمل",
        "In Progress": "جاری ہے",
        "Paid": "ادا شدہ",
        "Unpaid": "غیر ادا شدہ",
        "Partial": "جزوی",
        "Quick Actions": "فوری کارروائیاں",
        "Add Customer": "کسٹمر شامل کریں",
        "Customer List": "کسٹمرز کی فہرست",
        "Stock": "اسٹاک",
        "Sales": "فروخت",
        "Purchase": "خریداری",
        "Returns": "واپسی",
        "Barcodes": "بارکوڈز",
        "No sales recorded": "کوئی فروخت ریکارڈ نہیں",
        "No purchases recorded": "کوئی خریداری ریکارڈ نہیں",
        "More": "مزید",
        "Home": "ہوم",
        "Customers": "کسٹمرز",
        "Suppliers List": "سپلائرز کی فہرست",
        "Employees List": "ملازمین کی فہرست",
        "Purchases List": "خریداریوں کی فہرست",
        "Returns List": "واپسیوں کی فہرست",
        "Barcode Printing": "بارکوڈ پرنٹنگ",
        "Ledger": "لیجر",
      },
      ar: {
        "Dashboard": "لوحة التحكم",
        "Welcome": "مرحباً",
        "Services": "الخدمات",
        "Returns": "المرتجعات",
        "Employees": "الموظفين",
        "Transactions": "المعاملات",
        "Recent Sales": "المبيعات الأخيرة",
        "Recent Purchases": "المشتريات الأخيرة",
        "Low Stock Alerts": "تنبيهات المخزون المنخفض",
        "Today's Sales": "مبيعات اليوم",
        "Total Customers": "إجمالي العملاء",
        "Suppliers": "الموردين",
        "Products": "المنتجات",
        "Inventory Value": "قيمة المخزون",
        "Low Stock Alert": "تنبيه المخزون المنخفض",
        "Pending": "قيد الانتظار",
        "Completed": "مكتمل",
        "In Progress": "قيد التقدم",
        "Paid": "مدفوع",
        "Unpaid": "غير مدفوع",
        "Partial": "جزئي",
        "Quick Actions": "إجراءات سريعة",
        "Add Customer": "إضافة عميل",
        "Customer List": "قائمة العملاء",
        "Stock": "المخزون",
        "Sales": "المبيعات",
        "Purchase": "الشراء",
        "Returns": "المرتجعات",
        "Barcodes": "الباركود",
        "No sales recorded": "لا توجد مبيعات مسجلة",
        "No purchases recorded": "لا توجد مشتريات مسجلة",
        "More": "المزيد",
        "Home": "الرئيسية",
        "Customers": "العملاء",
        "Suppliers List": "قائمة الموردين",
        "Employees List": "قائمة الموظفين",
        "Purchases List": "قائمة المشتريات",
        "Returns List": "قائمة المرتجعات",
        "Barcode Printing": "طباعة الباركود",
        "Ledger": "دفتر الأستاذ",
      },
    };

    if (dict[targetLang] && dict[targetLang][text]) {
      return dict[targetLang][text];
    }

    try {
      const response = await fetch("https://translate.argosopentech.com/translate", {
        method: "POST",
        body: JSON.stringify({ q: text, source: "en", target: targetLang }),
        headers: { "Content-Type": "application/json" },
      });
      if (response.ok) {
        const data = await response.json();
        return data.translatedText;
      }
    } catch (e) {
      console.log("Translation failed");
    }
    return text;
  };

  const translateText = async (text, targetLang) => {
    const cacheKey = `${text}_${targetLang}`;
    if (cache.has(cacheKey)) {
      return cache.get(cacheKey);
    }
    const translated = await translateAPI(text, targetLang);
    cache.set(cacheKey, translated);
    saveToCache();
    return translated;
  };

  const defaultTexts = {
    dashboardTitle: "Dashboard",
    welcome: "Welcome",
    services: "Services",
    returns: "Returns",
    employees: "Employees",
    transactions: "Transactions",
    recentSales: "Recent Sales",
    recentPurchases: "Recent Purchases",
    lowStockAlerts: "Low Stock Alerts",
    todaysSales: "Today's Sales",
    totalCustomers: "Total Customers",
    suppliers: "Suppliers",
    products: "Products",
    inventoryValue: "Inventory Value",
    lowStockAlert: "Low Stock Alert",
    pending: "Pending",
    completed: "Completed",
    inProgress: "In Progress",
    paid: "Paid",
    unpaid: "Unpaid",
    partial: "Partial",
    quickActions: "Quick Actions",
    addCustomer: "Add Customer",
    customerList: "Customer List",
    stock: "Stock",
    sales: "Sales",
    purchase: "Purchase",
    barcodes: "Barcodes",
    noSales: "No sales recorded",
    noPurchases: "No purchases recorded",
    more: "More",
    home: "Home",
    customers: "Customers",
    suppliersList: "Suppliers List",
    employeesList: "Employees List",
    purchasesList: "Purchases List",
    returnsList: "Returns List",
    barcodePrinting: "Barcode Printing",
    ledger: "Ledger",
  };

  const getTranslations = async (lang) => {
    if (lang === "en") return defaultTexts;
    const translations = {};
    const promises = [];
    Object.keys(defaultTexts).forEach((key) => {
      const cacheKey = `${defaultTexts[key]}_${lang}`;
      if (cache.has(cacheKey)) {
        translations[key] = cache.get(cacheKey);
      } else {
        promises.push(
          translateText(defaultTexts[key], lang).then((translated) => {
            translations[key] = translated;
          })
        );
      }
    });
    if (promises.length > 0) {
      await Promise.all(promises);
      saveToCache();
    }
    return translations;
  };

  loadFromCache();
  return { getTranslations, translateText };
};

const dashboardTranslator = createDashboardTranslator();

// ==================== SHARED VISUAL HELPERS ====================
const handleSpotlightMove = (e) => {
  const card = e.currentTarget;
  const rect = card.getBoundingClientRect();
  card.style.setProperty("--mx", `${e.clientX - rect.left}px`);
  card.style.setProperty("--my", `${e.clientY - rect.top}px`);
};

const useCountUp = (rawValue, duration = 900) => {
  const [display, setDisplay] = useState(0);
  const prevRef = useRef(0);

  useEffect(() => {
    const end = typeof rawValue === "number" ? rawValue : parseFloat(rawValue) || 0;
    const start = prevRef.current;

    if (!Number.isFinite(end)) {
      setDisplay(0);
      return;
    }
    if (start === end) {
      setDisplay(end);
      return;
    }

    let raf;
    const startTime = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(start + (end - start) * eased);
      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        prevRef.current = end;
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [rawValue, duration]);

  return display;
};

// ==================== TOAST NOTIFICATION ====================
const DashboardToast = ({ toast, setToast }) => {
  if (!toast.show) return null;
  return (
    <div className={`ozanalyticsui-toast ${toast.type}`}>
      <div className="ozanalyticsui-toast-accent" />
      <div className="ozanalyticsui-toast-timer">
        <div className="ozanalyticsui-toast-progress-bar" />
      </div>
    </div>
  );
};

// ==================== NATIVE MOBILE HOOK ====================
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

// ==================== NATIVE BOTTOM SHEET ====================
const NativeBottomSheet = ({ isOpen, onClose, title, children, darkMode }) => {
  if (!isOpen) return null;

  return (
    <>
      <div className="oznative-overlay" onClick={onClose} />
      <div className={`oznative-bottom-sheet ${darkMode ? 'dark' : ''}`}>
        <div className="oznative-bottom-sheet-handle" />
        <div className="oznative-bottom-sheet-header">
          <h3>{title}</h3>
          <button className="oznative-bottom-sheet-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        <div className="oznative-bottom-sheet-body">
          {children}
        </div>
      </div>
    </>
  );
};

// ==================== NATIVE QUICK ACTIONS ====================
const NativeQuickActions = ({ onNavigate, translations }) => {
  const actions = [
    { 
      id: 'addcustomer', 
      icon: FaUserPlus, 
      label: translations.addCustomer || 'Add Customer',
      color: '#007aff',
      menu: 'customers',
      submenu: 'addcustomer'
    },
    { 
      id: 'customerlist', 
      icon: FaUsers, 
      label: translations.customerList || 'Customers',
      color: '#34c759',
      menu: 'customers',
      submenu: 'customerlist'
    },
    { 
      id: 'stock', 
      icon: FaBoxes, 
      label: translations.stock || 'Stock',
      color: '#ff9500',
      menu: 'stock',
      submenu: 'stocklist'
    },
    { 
      id: 'sales', 
      icon: FaShoppingCart, 
      label: translations.sales || 'Sales',
      color: '#8b5cf6',
      menu: 'sales',
      submenu: 'addsale'
    },
    { 
      id: 'purchase', 
      icon: FaTruck, 
      label: translations.purchase || 'Purchase',
      color: '#ef4444',
      menu: 'purchases',
      submenu: 'addpurchase'
    },
    { 
      id: 'returns', 
      icon: FaArrowCircleLeft, 
      label: translations.returns || 'Returns',
      color: '#ff2d55',
      menu: 'returns',
      submenu: 'addreturn'
    },
    { 
      id: 'transactions', 
      icon: FaMoneyBillWave, 
      label: translations.transactions || 'Transactions',
      color: '#5856d6',
      menu: 'transactions',
      submenu: 'Ledger'
    },
    { 
      id: 'barcode', 
      icon: FaQrcode, 
      label: translations.barcodes || 'Barcodes',
      color: '#0ea5e9',
      menu: 'barcode',
      submenu: 'barcodeprinting'
    },
  ];

  return (
    <div className="oznative-quick-actions">
      {actions.map((action) => (
        <button
          key={action.id}
          className="oznative-quick-action"
          onClick={() => {
            if (onNavigate) {
              onNavigate(action.menu, action.submenu);
            }
          }}
        >
          <div className="oznative-quick-action-icon" style={{ background: action.color }}>
            <action.icon />
          </div>
          <span className="oznative-quick-action-label">{action.label}</span>
        </button>
      ))}
    </div>
  );
};

// ==================== NATIVE SECTION HEADER ====================
const NativeSectionHeader = ({ title, count, onViewAll, viewAllLabel }) => (
  <div className="oznative-section-header">
    <div className="oznative-section-header-left">
      <h2 className="oznative-section-title">{title}</h2>
      {count !== undefined && count > 0 && (
        <span className="oznative-section-count">{count}</span>
      )}
    </div>
    {onViewAll && (
      <button className="oznative-section-action" onClick={onViewAll}>
        {viewAllLabel || 'View All'} →
      </button>
    )}
  </div>
);

// ==================== NATIVE LIST ITEM ====================
const NativeListItem = ({ 
  title, 
  subtitle, 
  avatar, 
  badge, 
  badgeColor, 
  onClick, 
  rightText,
  chevron = true 
}) => (
  <div className="oznative-list-item" onClick={onClick}>
    {avatar && (
      <div className="oznative-list-item-avatar" style={{ background: avatar.color || '#007aff' }}>
        {avatar.text}
      </div>
    )}
    <div className="oznative-list-item-content">
      <div className="oznative-list-item-title">{title}</div>
      {subtitle && <div className="oznative-list-item-subtitle">{subtitle}</div>}
    </div>
    <div className="oznative-list-item-right">
      {badge && (
        <span className={`oznative-list-item-badge ${badgeColor || ''}`}>
          {badge}
        </span>
      )}
      {rightText && <span className="oznative-list-item-price">{rightText}</span>}
      {chevron && <span className="oznative-list-item-chevron">›</span>}
    </div>
  </div>
);

// ==================== STAT CARD ====================
const StatCard = ({ 
  title, 
  value, 
  icon: Icon, 
  color, 
  subtitle, 
  onClick,
  clickable = false 
}) => {
  const currencyCards = [
    "Today's Sales",
    "Inventory Value",
    "Service Revenue",
    "Mobchange Revenue",
  ];
  const shouldShowCurrency = currencyCards.includes(title);
  const numericValue = typeof value === "number" ? value : parseFloat(value) || 0;
  const animatedValue = useCountUp(numericValue);

  const getColorClass = () => {
    const colorMap = {
      primary: "ozanalyticsui-stat-primary",
      success: "ozanalyticsui-stat-success",
      info: "ozanalyticsui-stat-info",
      warning: "ozanalyticsui-stat-warning",
      secondary: "ozanalyticsui-stat-secondary",
      purple: "ozanalyticsui-stat-purple",
      danger: "ozanalyticsui-stat-danger",
      teal: "ozanalyticsui-stat-teal",
    };
    return colorMap[color] || "ozanalyticsui-stat-primary";
  };

  const displayValue = shouldShowCurrency
    ? formatPKR(animatedValue)
    : Math.round(animatedValue).toLocaleString("en-PK");

  return (
    <div 
      className={`ozanalyticsui-stat-card ${getColorClass()} ${clickable ? 'ozanalyticsui-stat-clickable' : ''}`} 
      onMouseMove={handleSpotlightMove}
      onClick={clickable ? onClick : undefined}
      style={clickable ? { cursor: 'pointer' } : {}}
    >
      <div className="ozanalyticsui-stat-card-spotlight" />
      <div className="ozanalyticsui-stat-card-content">
        <div className="ozanalyticsui-stat-card-header">
          <h4>{title}</h4>
          <div className="ozanalyticsui-stat-card-icon">
            <Icon />
          </div>
        </div>
        <div className="ozanalyticsui-stat-card-value">{displayValue}</div>
        {subtitle && <div className="ozanalyticsui-stat-card-subtitle">{subtitle}</div>}
      </div>
      <div className="ozanalyticsui-stat-card-bg-glow"></div>
      <div className="ozanalyticsui-stat-card-accent-bar"></div>
    </div>
  );
};

// ==================== SKELETON LOADER ====================
const DashboardSkeleton = ({ darkMode }) => (
  <div className={`ozanalyticsui-container ${darkMode ? "dark" : "light"}`}>
    <div className="ozanalyticsui-bg-animation">
      <div className="ozanalyticsui-gradient-orb ozanalyticsui-orb-1"></div>
      <div className="ozanalyticsui-gradient-orb ozanalyticsui-orb-2"></div>
      <div className="ozanalyticsui-gradient-orb ozanalyticsui-orb-3"></div>
    </div>
    <div className="ozanalyticsui-skeleton ozanalyticsui-skeleton-header" />
    <div className="ozanalyticsui-stats-grid">
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="ozanalyticsui-skeleton ozanalyticsui-skeleton-card"
          style={{ animationDelay: `${i * 0.06}s` }}
        />
      ))}
    </div>
    <div className="ozanalyticsui-charts-row">
      <div className="ozanalyticsui-skeleton ozanalyticsui-skeleton-chart" />
      <div className="ozanalyticsui-skeleton ozanalyticsui-skeleton-chart" />
    </div>
  </div>
);

// ==================== MAIN COMPONENT ====================
const CosmeticsDashboardHome = ({ 
  onNavigate,
  setFormContent,
  onBreadcrumbUpdate
}) => {
  const { user, currentShop, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem("ourzest_language") || "en");
  const [translations, setTranslations] = useState({});
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("ourzest_darkMode") === "true");
  const [themeColor, setThemeColor] = useState(() => localStorage.getItem("ourzest_theme") || "#6366f1");
  const [toast, setToast] = useState({ show: false, type: "", message: "" });
  const [activeDateFilter, setActiveDateFilter] = useState("lastmonth");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  const [stats, setStats] = useState({
    totalSuppliers: 0,
    totalCustomers: 0,
    totalProducts: 0,
    lowStockCount: 0,
    inventoryValue: 0,
    todaySales: 0,
    todaySalesCount: 0,
    monthSales: 0,
    totalPurchases: 0,
  });

  const [moduleStats, setModuleStats] = useState({
    totalServices: 0,
    pendingServices: 0,
    completedServices: 0,
    inProgressServices: 0,
    totalMobchanges: 0,
    pendingMobchanges: 0,
    completedMobchanges: 0,
    totalReturns: 0,
    totalReturnsAmount: 0,
    totalEmployees: 0,
    activeEmployees: 0,
    totalSalaryPaid: 0,
    serviceRevenue: 0,
    mobchangeRevenue: 0,
  });

  const [salesTrendData, setSalesTrendData] = useState([]);
  const [recentSales, setRecentSales] = useState([]);
  const [recentPurchases, setRecentPurchases] = useState([]);
  const [recentServices, setRecentServices] = useState([]);
  const [recentMobchanges, setRecentMobchanges] = useState([]);
  const [recentReturns, setRecentReturns] = useState([]);
  const [recentEmployees, setRecentEmployees] = useState([]);
  const [customerTransactions, setCustomerTransactions] = useState([]);
  const [supplierTransactions, setSupplierTransactions] = useState([]);
  const [employeePayments, setEmployeePayments] = useState([]);
  const [expenseTransactions, setExpenseTransactions] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);

  const [filters, setFilters] = useState({ startDate: "", endDate: "", category: "all", paymentStatus: "all" });

  // ============================================
  // NATIVE STATE
  // ============================================
  const isMobile = useIsMobile();
  const [activeSheet, setActiveSheet] = useState(null);

  // ============================================
  // NAVIGATION HANDLERS
  // ============================================
  const navigateToMenu = useCallback((menuName, submenuName = null) => {
    if (onNavigate) {
      onNavigate(menuName, submenuName);
    }
  }, [onNavigate]);

  const openList = useCallback((listType) => {
    const navigationMap = {
      "Today's Sales": { menu: 'sales', submenu: 'saleslist' },
      "Total Customers": { menu: 'customers', submenu: 'customerlist' },
      "Suppliers": { menu: 'suppliers', submenu: 'supplierlist' },
      "Total Returns": { menu: 'returns', submenu: 'returnlist' },
      "Total Employees": { menu: 'employees', submenu: 'employeeslist' },
      "Products": { menu: 'stock', submenu: 'stocklist' },
      "Low Stock Alert": { menu: 'stock', submenu: 'stocklist' },
      "Inventory Value": { menu: 'stock', submenu: 'stocklist' },
    };

    const nav = navigationMap[listType];
    if (nav) {
      navigateToMenu(nav.menu, nav.submenu);
    }
  }, [navigateToMenu]);

  // ============================================
  // DATE FILTER
  // ============================================
  const getDateRangeFromFilter = useCallback((filter) => {
    const today = new Date();
    let startDate = "";
    let endDate = today.toISOString().split("T")[0];

    switch (filter) {
      case "today":
        startDate = endDate;
        break;
      case "lastweek":
        const weekAgo = new Date(today);
        weekAgo.setDate(today.getDate() - 7);
        startDate = weekAgo.toISOString().split("T")[0];
        break;
      case "lastmonth":
        const monthAgo = new Date(today);
        monthAgo.setMonth(today.getMonth() - 1);
        startDate = monthAgo.toISOString().split("T")[0];
        break;
      case "last6months":
        const sixMonthsAgo = new Date(today);
        sixMonthsAgo.setMonth(today.getMonth() - 6);
        startDate = sixMonthsAgo.toISOString().split("T")[0];
        break;
      case "custom":
        startDate = customStartDate;
        endDate = customEndDate;
        break;
      default:
        const defaultMonthAgo = new Date(today);
        defaultMonthAgo.setMonth(today.getMonth() - 1);
        startDate = defaultMonthAgo.toISOString().split("T")[0];
    }
    return { startDate, endDate };
  }, [customStartDate, customEndDate]);

  // ============================================
  // THEME COLOR AUTO-SYNC
  // ============================================
  const applyTheme = useCallback((color) => {
    if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
      color = "#6366f1";
    }
    document.documentElement.style.setProperty("--ozanalyticsui-primary-color", color);
    document.documentElement.style.setProperty("--ozanalyticsui-primary-dark", color);
    document.documentElement.style.setProperty("--ozanalyticsui-primary-light", color);

    let r, g, b;
    if (color.startsWith("#")) {
      r = parseInt(color.slice(1, 3), 16);
      g = parseInt(color.slice(3, 5), 16);
      b = parseInt(color.slice(5, 7), 16);
    } else {
      r = 99; g = 102; b = 241;
    }

    let style = document.getElementById("dynamic-orb-styles");
    if (!style) {
      style = document.createElement("style");
      style.id = "dynamic-orb-styles";
      document.head.appendChild(style);
    }

    style.textContent = `
      .ozanalyticsui-bg-animation .ozanalyticsui-gradient-orb { opacity: 0.4; }
      .ozanalyticsui-bg-animation .ozanalyticsui-gradient-orb.ozanalyticsui-orb-1 { background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.4) 0%, rgba(${r}, ${g}, ${b}, 0.05) 100%); }
      .ozanalyticsui-bg-animation .ozanalyticsui-gradient-orb.ozanalyticsui-orb-2 { background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.35) 0%, rgba(${r}, ${g}, ${b}, 0.04) 100%); }
      .ozanalyticsui-bg-animation .ozanalyticsui-gradient-orb.ozanalyticsui-orb-3 { background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.3) 0%, rgba(${r}, ${g}, ${b}, 0.03) 100%); }
      .ozanalyticsui-bg-animation .ozanalyticsui-gradient-orb.ozanalyticsui-orb-4 { background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.25) 0%, rgba(${r}, ${g}, ${b}, 0.02) 100%); }
      .ozanalyticsui-bg-animation .ozanalyticsui-gradient-orb.ozanalyticsui-orb-5 { background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.2) 0%, rgba(${r}, ${g}, ${b}, 0.01) 100%); }
      
      .ozanalyticsui-stat-primary .ozanalyticsui-stat-card-icon { color: ${color} !important; }
      .ozanalyticsui-stat-primary .ozanalyticsui-stat-card-accent-bar { background: ${color} !important; }
      
      .oznative-section-action { color: ${color} !important; }
      .oznative-bottom-nav-item.active { color: ${color} !important; }
    `;
  }, []);

  // ============================================
  // DARK MODE AUTO-SYNC
  // ============================================
  const applyDarkMode = useCallback((isDark) => {
    const container = document.querySelector(".ozanalyticsui-container");
    if (container) {
      if (isDark) {
        container.classList.add("dark");
        container.classList.remove("light");
      } else {
        container.classList.add("light");
        container.classList.remove("dark");
      }
    }

    if (isDark) {
      document.body.classList.add("dashboard-dark");
      document.documentElement.classList.add("dashboard-dark");
    } else {
      document.body.classList.remove("dashboard-dark");
      document.documentElement.classList.remove("dashboard-dark");
    }
  }, []);

  // ============================================
  // LANGUAGE AUTO-SYNC WITH RTL
  // ============================================
  const loadTranslations = useCallback(async (lang) => {
    const trans = await dashboardTranslator.getTranslations(lang);
    setTranslations(trans);
  }, []);

  const applyRTL = useCallback((lang) => {
    const isRTL = lang === "ur" || lang === "ar";
    const container = document.querySelector(".ozanalyticsui-container");
    if (container) {
      if (isRTL) {
        container.classList.add("rtl");
        container.classList.remove("ltr");
      } else {
        container.classList.add("ltr");
        container.classList.remove("rtl");
      }
    }

    if (isRTL) {
      document.documentElement.dir = "rtl";
      document.body.dir = "rtl";
    } else {
      document.documentElement.dir = "ltr";
      document.body.dir = "ltr";
    }
  }, []);

  // ============================================
  // INITIAL SETUP AND SYNC
  // ============================================
  useEffect(() => {
    const savedTheme = localStorage.getItem("ourzest_theme") || "#6366f1";
    const savedDarkMode = localStorage.getItem("ourzest_darkMode") === "true";
    const savedLanguage = localStorage.getItem("ourzest_language") || "en";

    setThemeColor(savedTheme);
    setDarkMode(savedDarkMode);
    setCurrentLanguage(savedLanguage);

    applyTheme(savedTheme);
    applyDarkMode(savedDarkMode);
    loadTranslations(savedLanguage);
    applyRTL(savedLanguage);
  }, []);

  // ============================================
  // LISTEN FOR EXTERNAL CHANGES
  // ============================================
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "ourzest_language") {
        const newLang = e.newValue || "en";
        if (newLang !== currentLanguage) {
          setCurrentLanguage(newLang);
          loadTranslations(newLang);
          applyRTL(newLang);
        }
      }
      if (e.key === "ourzest_darkMode") {
        const isDark = e.newValue === "true";
        if (isDark !== darkMode) {
          setDarkMode(isDark);
          applyDarkMode(isDark);
        }
      }
      if (e.key === "ourzest_theme") {
        const newTheme = e.newValue || "#6366f1";
        if (newTheme !== themeColor) {
          setThemeColor(newTheme);
          applyTheme(newTheme);
        }
      }
    };

    const handleLanguageChanged = (event) => {
      if (event.detail && event.detail.language) {
        const newLang = event.detail.language;
        if (newLang !== currentLanguage) {
          setCurrentLanguage(newLang);
          loadTranslations(newLang);
          applyRTL(newLang);
        }
      }
    };

    const handleDarkModeToggled = (event) => {
      if (event.detail && event.detail.darkMode !== undefined) {
        const isDark = event.detail.darkMode;
        if (isDark !== darkMode) {
          setDarkMode(isDark);
          applyDarkMode(isDark);
        }
      }
    };

    const handleThemeChanged = (event) => {
      if (event.detail && event.detail.theme) {
        const newTheme = event.detail.theme;
        if (newTheme !== themeColor) {
          setThemeColor(newTheme);
          applyTheme(newTheme);
        }
      }
    };

    // Poll for changes
    const interval = setInterval(() => {
      const storedLanguage = localStorage.getItem("ourzest_language") || "en";
      const storedDarkMode = localStorage.getItem("ourzest_darkMode") === "true";
      const storedTheme = localStorage.getItem("ourzest_theme") || "#6366f1";

      if (storedLanguage !== currentLanguage) {
        setCurrentLanguage(storedLanguage);
        loadTranslations(storedLanguage);
        applyRTL(storedLanguage);
      }

      if (storedDarkMode !== darkMode) {
        setDarkMode(storedDarkMode);
        applyDarkMode(storedDarkMode);
      }

      if (storedTheme !== themeColor) {
        setThemeColor(storedTheme);
        applyTheme(storedTheme);
      }
    }, 1000);

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("languageChanged", handleLanguageChanged);
    window.addEventListener("darkModeToggled", handleDarkModeToggled);
    window.addEventListener("themeChanged", handleThemeChanged);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("languageChanged", handleLanguageChanged);
      window.removeEventListener("darkModeToggled", handleDarkModeToggled);
      window.removeEventListener("themeChanged", handleThemeChanged);
      clearInterval(interval);
    };
  }, [currentLanguage, darkMode, themeColor, applyDarkMode, applyTheme, loadTranslations, applyRTL]);

  // ============================================
  // TOAST
  // ============================================
  const showToast = (type, message) => {
    setToast({ show: true, type, message });
    setTimeout(() => setToast({ show: false }), 3000);
  };

  // ============================================
  // LOAD DASHBOARD DATA
  // ============================================
  const loadDashboardData = useCallback(async () => {
    if (!user || !currentShop) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const shopId = currentShop?.shop_id;

      const { startDate, endDate } = getDateRangeFromFilter(activeDateFilter);

      if (activeDateFilter === 'custom' && (!customStartDate || !customEndDate)) {
        setLoading(false);
        return;
      }

      const updatedFilters = {
        startDate,
        endDate,
        category: filters.category,
        paymentStatus: filters.paymentStatus
      };

      setAuthData(user, currentShop);

      const [dashboardData, servicesData, mobchangeData, returnsData, employeesData, transactionsData, purchasesData] =
        await Promise.all([
          fetchDashboardData(updatedFilters),
          fetchServicesData(shopId, user.username, updatedFilters),
          fetchMobchangeData(shopId, user.username, updatedFilters),
          fetchReturnsData(shopId, user.username, updatedFilters),
          fetchEmployeesData(shopId, user.username),
          fetchTransactionsData(shopId, updatedFilters),
          fetchPurchasesData(shopId, user.username, updatedFilters),
        ]);

      if (dashboardData && dashboardData.stats) {
        setStats({
          totalSuppliers: dashboardData.stats?.total_suppliers || 0,
          totalCustomers: dashboardData.stats?.total_customers || 0,
          totalProducts: dashboardData.stats?.total_products || 0,
          lowStockCount: dashboardData.stats?.low_stock_count || 0,
          inventoryValue: dashboardData.stats?.inventory_value || 0,
          todaySales: dashboardData.stats?.today_sales || 0,
          todaySalesCount: dashboardData.stats?.today_sales_count || 0,
          monthSales: dashboardData.stats?.month_sales || 0,
          totalPurchases: dashboardData.stats?.total_purchases || 0,
        });

        const labels = dashboardData.trend?.labels || [];
        const values = dashboardData.trend?.values || [];
        
        const formattedTrendData = labels.map((label, index) => ({
          name: label,
          revenue: values[index] || 0,
        }));

        setSalesTrendData(formattedTrendData);
        setRecentSales(dashboardData.recent_sales || []);
        setLowStockItems(dashboardData.low_stock || []);
      }

      if (servicesData) {
        setModuleStats(prev => ({
          ...prev,
          totalServices: servicesData.total || 0,
          pendingServices: servicesData.pending || 0,
          completedServices: servicesData.completed || 0,
          inProgressServices: servicesData.in_progress || 0,
          serviceRevenue: servicesData.total_revenue || 0,
        }));
        setRecentServices(servicesData.recent || []);
      }

      if (mobchangeData) {
        setModuleStats(prev => ({
          ...prev,
          totalMobchanges: mobchangeData.total || 0,
          pendingMobchanges: mobchangeData.pending || 0,
          completedMobchanges: mobchangeData.completed || 0,
          mobchangeRevenue: mobchangeData.total_revenue || 0,
        }));
        setRecentMobchanges(mobchangeData.recent || []);
      }

      if (returnsData) {
        setModuleStats(prev => ({
          ...prev,
          totalReturns: returnsData.total || 0,
          totalReturnsAmount: returnsData.total_amount || 0,
        }));
        setRecentReturns(returnsData.recent || []);
      }

      if (employeesData) {
        setModuleStats(prev => ({
          ...prev,
          totalEmployees: employeesData.total || 0,
          activeEmployees: employeesData.active || 0,
          totalSalaryPaid: employeesData.total_salary_paid || 0,
        }));
        setRecentEmployees(employeesData.recent || []);
      }

      if (transactionsData) {
        setCustomerTransactions(transactionsData.customer || []);
        setSupplierTransactions(transactionsData.supplier || []);
        setEmployeePayments(transactionsData.employee || []);
        setExpenseTransactions(transactionsData.expense || []);
      }

      if (purchasesData) {
        setRecentPurchases(purchasesData.recent || []);
      }

      const filterName = activeDateFilter === "today" ? "Today" :
                         activeDateFilter === "lastweek" ? "Last Week" :
                         activeDateFilter === "lastmonth" ? "Last Month" :
                         activeDateFilter === "last6months" ? "Last 6 Months" : "Custom Range";

      showToast("success", `Dashboard updated for ${filterName}`);
    } catch (error) {
      console.error("Error loading dashboard:", error);
      showToast("error", error.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [user, currentShop, filters, activeDateFilter, customStartDate, customEndDate, getDateRangeFromFilter]);

  useEffect(() => {
    if (!authLoading && user && currentShop) {
      loadDashboardData();
    }
  }, [loadDashboardData, authLoading, user, currentShop]);

  useEffect(() => {
    if (user && currentShop && !authLoading && !loading) {
      loadDashboardData();
    }
  }, [activeDateFilter, customStartDate, customEndDate]);

  // ============================================
  // RENDER
  // ============================================
  if (authLoading || loading) {
    return <DashboardSkeleton darkMode={darkMode} />;
  }

  return (
    <div className={`ozanalyticsui-container ${darkMode ? "dark" : "light"} ${currentLanguage === "ur" || currentLanguage === "ar" ? "rtl" : "ltr"}`}>
      <DashboardToast toast={toast} setToast={setToast} />

      {/* Animated Background */}
      <div className="ozanalyticsui-bg-animation">
        <div className="ozanalyticsui-grain-overlay"></div>
        <div className="ozanalyticsui-gradient-orb ozanalyticsui-orb-1"></div>
        <div className="ozanalyticsui-gradient-orb ozanalyticsui-orb-2"></div>
        <div className="ozanalyticsui-gradient-orb ozanalyticsui-orb-3"></div>
        <div className="ozanalyticsui-gradient-orb ozanalyticsui-orb-4"></div>
        <div className="ozanalyticsui-gradient-orb ozanalyticsui-orb-5"></div>
      </div>

      {/* NATIVE HEADER - Only on mobile */}
      {isMobile && (
        <div className="oznative-header">
          <div className="oznative-header-content">
            <div>
              <div className="oznative-header-title">
                {translations.dashboardTitle || "Dashboard"}
              </div>
              <div className="oznative-header-subtitle">
                {translations.welcome || "Welcome"}, {user?.fullname || user?.username || 'User'} 👋
              </div>
            </div>
            <div className="oznative-header-actions">
              <button className="oznative-header-icon-btn" onClick={() => {}}>
                <FaBell />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Header - Keep existing */}
      {!isMobile && (
        <div className="ozanalyticsui-header">
          <div className="ozanalyticsui-title-section">
            <div className="ozanalyticsui-title-text">
              <h1>{translations.dashboardTitle || "Analytics Dashboard"}</h1>
              <p>{translations.welcome || "Welcome"}, {user?.fullname || user?.username || 'User'} 👋</p>
            </div>
          </div>
        </div>
      )}

      {/* NATIVE SUMMARY CARDS - Mobile only */}
      {isMobile ? (
        <div className="oznative-summary-grid">
          <div className="oznative-summary-card" onClick={() => openList("Today's Sales")}>
            <div className="oznative-summary-card-icon blue"><FaShoppingCart /></div>
            <div className="oznative-summary-card-content">
              <div className="oznative-summary-card-value">{formatPKR(stats.todaySales)}</div>
              <div className="oznative-summary-card-label">{translations.todaysSales || "Today's Sales"}</div>
            </div>
          </div>
          <div className="oznative-summary-card" onClick={() => openList("Total Customers")}>
            <div className="oznative-summary-card-icon green"><FaUsers /></div>
            <div className="oznative-summary-card-content">
              <div className="oznative-summary-card-value">{stats.totalCustomers}</div>
              <div className="oznative-summary-card-label">{translations.totalCustomers || "Total Customers"}</div>
            </div>
          </div>
          <div className="oznative-summary-card" onClick={() => openList("Products")}>
            <div className="oznative-summary-card-icon orange"><FaBoxes /></div>
            <div className="oznative-summary-card-content">
              <div className="oznative-summary-card-value">{stats.totalProducts}</div>
              <div className="oznative-summary-card-label">{translations.products || "Products"}</div>
            </div>
          </div>
          <div className="oznative-summary-card" onClick={() => openList("Low Stock Alert")}>
            <div className="oznative-summary-card-icon red"><FiAlertCircle /></div>
            <div className="oznative-summary-card-content">
              <div className="oznative-summary-card-value">{stats.lowStockCount}</div>
              <div className="oznative-summary-card-label">{translations.lowStockAlert || "Low Stock"}</div>
            </div>
          </div>
          <div className="oznative-summary-card" onClick={() => openList("Total Returns")}>
            <div className="oznative-summary-card-icon purple"><FaArrowCircleLeft /></div>
            <div className="oznative-summary-card-content">
              <div className="oznative-summary-card-value">{moduleStats.totalReturns}</div>
              <div className="oznative-summary-card-label">{translations.returns || "Returns"}</div>
            </div>
          </div>
          <div className="oznative-summary-card" onClick={() => openList("Total Employees")}>
            <div className="oznative-summary-card-icon teal"><FaUserCog /></div>
            <div className="oznative-summary-card-content">
              <div className="oznative-summary-card-value">{moduleStats.totalEmployees}</div>
              <div className="oznative-summary-card-label">{translations.employees || "Employees"}</div>
            </div>
          </div>
        </div>
      ) : (
        // Desktop Stats Grid
        <div className="ozanalyticsui-stats-grid">
          <StatCard
            title={translations.todaysSales || "Today's Sales"}
            value={stats.todaySales}
            icon={FaShoppingCart}
            color="primary"
            subtitle={`${stats.todaySalesCount} invoices`}
            clickable={true}
            onClick={() => openList("Today's Sales")}
          />
          <StatCard
            title={translations.totalCustomers || "Total Customers"}
            value={stats.totalCustomers}
            icon={FaUsers}
            color="info"
            clickable={true}
            onClick={() => openList("Total Customers")}
          />
          <StatCard
            title={translations.suppliers || "Suppliers"}
            value={stats.totalSuppliers}
            icon={FaTruck}
            color="warning"
            clickable={true}
            onClick={() => openList("Suppliers")}
          />
          <StatCard
            title={translations.totalReturns || "Total Returns"}
            value={moduleStats.totalReturns}
            icon={FaArrowCircleLeft}
            color="danger"
            subtitle={`Amount: ${formatPKR(moduleStats.totalReturnsAmount)}`}
            clickable={true}
            onClick={() => openList("Total Returns")}
          />
          <StatCard
            title={translations.totalEmployees || "Total Employees"}
            value={moduleStats.totalEmployees}
            icon={FaUserCog}
            color="teal"
            subtitle={`Active: ${moduleStats.activeEmployees}`}
            clickable={true}
            onClick={() => openList("Total Employees")}
          />
          <StatCard
            title={translations.products || "Products"}
            value={stats.totalProducts}
            icon={FaBoxes}
            color="secondary"
            subtitle={`Total: ${stats.totalProducts}`}
            clickable={true}
            onClick={() => openList("Products")}
          />
          <StatCard
            title={translations.inventoryValue || "Inventory Value"}
            value={stats.inventoryValue}
            icon={MdInventory}
            color="purple"
            clickable={true}
            onClick={() => openList("Inventory Value")}
          />
          <StatCard
            title={translations.lowStockAlert || "Low Stock Alert"}
            value={stats.lowStockCount}
            icon={FiAlertCircle}
            color="danger"
            subtitle="Need reorder"
            clickable={true}
            onClick={() => openList("Low Stock Alert")}
          />
        </div>
      )}

      {/* QUICK ACTIONS - Mobile only */}
      {isMobile && (
        <div className="oznative-quick-actions-section">
          <NativeSectionHeader 
            title={translations.quickActions || "Quick Actions"}
          />
          <NativeQuickActions onNavigate={navigateToMenu} translations={translations} />
        </div>
      )}

      {/* RECENT SALES - REMOVED entirely */}

      {/* RECENT PURCHASES - REMOVED entirely */}

      {/* LOW STOCK - Mobile Native List */}
      {isMobile && lowStockItems.length > 0 && (
        <>
          <NativeSectionHeader 
            title={translations.lowStockAlerts || "Low Stock Alerts"}
            count={lowStockItems.length}
            onViewAll={() => openList("Low Stock Alert")}
          />
          <div className="oznative-list">
            {lowStockItems.slice(0, 5).map((product) => (
              <NativeListItem
                key={product.id}
                title={product.product_name}
                subtitle={`Batch: ${product.batch_no || '-'} • Alert: ${product.quantity_alert || 5}`}
                avatar={{ text: '⚠', color: '#ef4444' }}
                rightText={`${product.quantity} ${product.unit || 'units'}`}
                onClick={() => openList("Low Stock Alert")}
              />
            ))}
          </div>
        </>
      )}

      {/* BOTTOM NAVIGATION - Mobile only */}
      {isMobile && (
        <div className="oznative-bottom-nav">
          <button className="oznative-bottom-nav-item active">
            <FaHome />
            <span>{translations.home || "Home"}</span>
          </button>
          <button className="oznative-bottom-nav-item" onClick={() => navigateToMenu('customers', 'customerlist')}>
            <FaUsers />
            <span>{translations.customers || "Customers"}</span>
          </button>
          <button className="oznative-bottom-nav-item" onClick={() => navigateToMenu('stock', 'stocklist')}>
            <FaBoxes />
            <span>{translations.stock || "Stock"}</span>
          </button>
          <button className="oznative-bottom-nav-item" onClick={() => navigateToMenu('sales', 'addsale')}>
            <FaShoppingCart />
            <span>{translations.sales || "Sales"}</span>
          </button>
          <button className="oznative-bottom-nav-item" onClick={() => setActiveSheet('more')}>
            <FaEllipsisH />
            <span>{translations.more || "More"}</span>
          </button>
        </div>
      )}

      {/* MORE BOTTOM SHEET */}
      <NativeBottomSheet
        isOpen={activeSheet === 'more'}
        onClose={() => setActiveSheet(null)}
        title={translations.more || "More Actions"}
        darkMode={darkMode}
      >
        <div className="oznative-more-grid">
          {[
            { id: 'addcustomer', label: translations.addCustomer || 'Add Customer', icon: FaUserPlus, color: '#007aff', menu: 'customers', submenu: 'addcustomer' },
            { id: 'customerlist', label: translations.customerList || 'Customer List', icon: FaUsers, color: '#34c759', menu: 'customers', submenu: 'customerlist' },
            { id: 'suppliers', label: translations.suppliersList || 'Suppliers', icon: FaTruck, color: '#ff9500', menu: 'suppliers', submenu: 'supplierlist' },
            { id: 'employees', label: translations.employeesList || 'Employees', icon: FaUserCog, color: '#5856d6', menu: 'employees', submenu: 'employeeslist' },
            { id: 'purchases', label: translations.purchasesList || 'Purchases', icon: FaShoppingCart, color: '#ef4444', menu: 'purchases', submenu: 'purchaselist' },
            { id: 'returns', label: translations.returnsList || 'Returns', icon: FaArrowCircleLeft, color: '#ff2d55', menu: 'returns', submenu: 'returnlist' },
            { id: 'transactions', label: translations.ledger || 'Ledger', icon: FaMoneyBillWave, color: '#8b5cf6', menu: 'transactions', submenu: 'Ledger' },
            { id: 'barcode', label: translations.barcodePrinting || 'Barcodes', icon: FaQrcode, color: '#0ea5e9', menu: 'barcode', submenu: 'barcodeprinting' },
          ].map((item) => (
            <button
              key={item.id}
              className="oznative-more-item"
              onClick={() => {
                setActiveSheet(null);
                navigateToMenu(item.menu, item.submenu);
              }}
            >
              <div className="oznative-more-item-icon" style={{ background: item.color }}>
                <item.icon />
              </div>
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      </NativeBottomSheet>
    </div>
  );
};

export default CosmeticsDashboardHome;